/**
 * Ethereal Audio System for the 7 Halls of Heaven
 *
 * Generates heavenly ambient music procedurally using Web Audio API.
 * Each hall has a unique harmonic signature — as the player moves between
 * halls, the audio crossfades smoothly.
 */

// Musical scales / chord tones per hall (frequencies in Hz)
const HALL_AUDIO = {
  seraphim: {
    // Bright, fiery, high — Lydian mode overtones
    baseFreq: 220,
    chordRatios: [1, 1.25, 1.5, 1.875, 2, 2.5, 3],
    padType: 'sine',
    reverbTime: 4,
    filterFreq: 3000,
    tempo: 0.3,
    description: 'Seraphic fire chords',
  },
  cherubim: {
    // Deep, wise, contemplative — minor 7th voicings
    baseFreq: 146.83,
    chordRatios: [1, 1.2, 1.5, 1.8, 2, 2.4],
    padType: 'sine',
    reverbTime: 5,
    filterFreq: 1800,
    tempo: 0.2,
    description: 'Wisdom of the deep',
  },
  thrones: {
    // Majestic, powerful — perfect 5ths stacked
    baseFreq: 196,
    chordRatios: [1, 1.5, 2, 3, 4, 6],
    padType: 'sine',
    reverbTime: 6,
    filterFreq: 2200,
    tempo: 0.15,
    description: 'Throne room majesty',
  },
  dominions: {
    // Orderly, regal — major triads
    baseFreq: 261.63,
    chordRatios: [1, 1.25, 1.5, 2, 2.5, 3],
    padType: 'sine',
    reverbTime: 4,
    filterFreq: 2500,
    tempo: 0.25,
    description: 'Order of the spheres',
  },
  virtues: {
    // Nature, flowing, bright — pentatonic feel
    baseFreq: 174.61,
    chordRatios: [1, 1.125, 1.333, 1.5, 2, 2.25, 2.667],
    padType: 'sine',
    reverbTime: 5,
    filterFreq: 3500,
    tempo: 0.35,
    description: 'Gardens of miracle',
  },
  powers: {
    // Strong, protective, deep — power chords
    baseFreq: 130.81,
    chordRatios: [1, 1.5, 2, 3, 4],
    padType: 'triangle',
    reverbTime: 3,
    filterFreq: 1500,
    tempo: 0.18,
    description: 'Shield of light',
  },
  principalities: {
    // Ethereal, universal, open — sus2/sus4 voicings
    baseFreq: 164.81,
    chordRatios: [1, 1.125, 1.5, 2, 2.25, 3, 4],
    padType: 'sine',
    reverbTime: 7,
    filterFreq: 2800,
    tempo: 0.12,
    description: 'Watchers beyond the veil',
  },
};

export class HeavenAudio {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.currentHall = null;
    this.activeVoices = [];
    this.reverb = null;
    this.initialized = false;
    this.volume = 0.25;
    this.fadeTime = 3; // seconds for crossfade
  }

  async init() {
    if (this.initialized) return;

    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();

      // Master gain
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0;
      this.masterGain.connect(this.ctx.destination);

      // Create reverb
      this.reverb = await this._createReverb(5);

      this.initialized = true;
      console.log('✓ Heaven audio system initialized');
    } catch (e) {
      console.warn('⚠ Audio system failed to initialize:', e.message);
    }
  }

  async _createReverb(duration) {
    const sampleRate = this.ctx.sampleRate;
    const length = sampleRate * duration;
    const impulse = this.ctx.createBuffer(2, length, sampleRate);

    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
      }
    }

    const convolver = this.ctx.createConvolver();
    convolver.buffer = impulse;
    return convolver;
  }

  // Start playing a hall's ambient pad
  playHall(hallName) {
    if (!this.initialized || hallName === this.currentHall) return;

    // Resume audio context if suspended (browser autoplay policy)
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    const config = HALL_AUDIO[hallName];
    if (!config) return;

    // Fade out current voices
    this._fadeOutAll();

    this.currentHall = hallName;

    // Create new pad voices
    const now = this.ctx.currentTime;
    const voices = [];

    // Pad layer — sustained chord tones
    for (const ratio of config.chordRatios) {
      const freq = config.baseFreq * ratio;
      const osc = this.ctx.createOscillator();
      osc.type = config.padType;
      osc.frequency.value = freq;

      // Gentle detuning for richness
      osc.detune.value = (Math.random() - 0.5) * 8;

      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.gain.linearRampToValueAtTime(
        this.volume / config.chordRatios.length * 0.5,
        now + this.fadeTime
      );

      // Low pass filter for warmth
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = config.filterFreq;
      filter.Q.value = 0.5;

      osc.connect(filter);
      filter.connect(gain);

      // Connect through reverb
      const reverbGain = this.ctx.createGain();
      reverbGain.gain.value = 0.3;
      gain.connect(reverbGain);
      reverbGain.connect(this.reverb);
      this.reverb.connect(this.masterGain);

      // Also direct signal
      gain.connect(this.masterGain);

      // Fade master in
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.linearRampToValueAtTime(1, now + this.fadeTime);

      osc.start(now);
      voices.push({ osc, gain, filter, reverbGain });
    }

    // LFO to slowly modulate filter for movement
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = config.tempo;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = config.filterFreq * 0.3;
    lfo.connect(lfoGain);

    for (const voice of voices) {
      lfoGain.connect(voice.filter.frequency);
    }
    lfo.start(now);
    voices.push({ osc: lfo, gain: lfoGain, isLFO: true });

    this.activeVoices = voices;
  }

  // Fade out and stop all current voices
  _fadeOutAll() {
    if (!this.ctx || this.activeVoices.length === 0) return;
    const now = this.ctx.currentTime;

    for (const voice of this.activeVoices) {
      try {
        if (voice.gain && !voice.isLFO) {
          voice.gain.gain.cancelScheduledValues(now);
          voice.gain.gain.linearRampToValueAtTime(0, now + this.fadeTime);
        }
        // Stop oscillator after fade
        voice.osc.stop(now + this.fadeTime + 0.1);
      } catch (_) {
        // Already stopped
      }
    }

    this.activeVoices = [];
  }

  // Stop playing (leaving all halls)
  stopAll() {
    this._fadeOutAll();
    this.currentHall = null;
    if (this.masterGain) {
      const now = this.ctx.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.linearRampToValueAtTime(0, now + this.fadeTime);
    }
  }

  // Call from the game loop with the player's current position
  updateForPosition(playerPosition, heavenEnv) {
    if (!this.initialized || !heavenEnv) return;

    const zone = heavenEnv.getCurrentAudioZone(playerPosition);
    if (zone) {
      this.playHall(zone.hallName);
    } else if (this.currentHall) {
      this.stopAll();
    }
  }

  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.masterGain) {
      this.masterGain.gain.value = this.volume > 0 ? 1 : 0;
    }
  }

  destroy() {
    this._fadeOutAll();
    if (this.ctx) {
      this.ctx.close();
    }
  }
}
